// services/bankcard.service.js
import { 
  fetchBucketContent, 
  fetchFileContent, 
  deleteFile, 
  encryptData, 
  decryptData,
  removeTxtExtension,
  ensureTxtExtension,
  clearBucketContentCache,
  getUserCredentials,
  ensureS3SafeFileName,
  formatFileSize
} from './api.service';

import { 
  getCurrentInheritance, 
  uploadInheritance
} from './endowment.service';

import CryptoJS from 'crypto-js';
import { UPLOAD_ENDPOINTS, CONTENT_ENDPOINTS } from '../utils/constants';

const BUCKET_NAME = 'bankcards';

// Fetch all bank cards
export const fetchBankCards = async (token, forceClearCache = false) => {
  try {
    return await fetchBucketContent(BUCKET_NAME, token, forceClearCache);
  } catch (error) {
    console.error('Error fetching passwords:', error);
    throw error;
  }
};

// View a specific bank card
export const viewBankCard = async (cardName, token) => {
  try {
    const fileContent = await fetchFileContent(BUCKET_NAME, cardName, token);
    return parseBankCardContent(fileContent, cardName);
  } catch (error) {
    console.error('Error viewing bank card:', error);
    throw error;
  }
};

// Parse bank card file content
const parseBankCardContent = (fileContent, cardName) => {
  try {
    const lines = fileContent.split(',');
    
    // Extract fields from the content
    const alias = lines[0];
    const encryptedCardNumber = lines[1];
    const encryptedCardCcv = lines[2];
    const encryptedCardExp = lines[3];
    const cardHolderName = lines[4] || '';
    const street = lines[5] || '';
    const city = lines[6] || '';
    const state = lines[7] || '';
    const zip = lines[8] || '';
    const country = lines[9] || '';
    const accountNumber = lines[10] || '';
    
    // Decrypt sensitive data
    const cardNumber = decryptData(encryptedCardNumber);
    const cardCcv = decryptData(encryptedCardCcv);
    const cardExp = decryptData(encryptedCardExp);
    
    return {
      alias: removeTxtExtension(alias),
      cardNumber,
      cardCcv,
      cardExp,
      cardHolderName,
      street,
      city,
      state,
      zip,
      country,
      accountNumber,
      originalName: cardName // Keep the original name for updates
    };
  } catch (error) {
    console.error('Error parsing bank card content:', error);
    throw new Error('Invalid bank card file format');
  }
};

// Save bank card (create or update)
export const saveBankCard = async (cardData, originalName = null, token) => {
  try {
    let credentials;
    if (!token) {
      // Try to get from session storage as fallback
      credentials = getUserCredentials();
      if (!credentials) {
        throw new Error('No authentication information found');
      }
      token = credentials.token;
    }
    
    
    if (originalName) {
      await deleteBankCard(originalName, token);
    }
    
    // Encrypt sensitive data
    const encryptedCardNumber = encryptData(cardData.cardNumber);
    const encryptedCardCcv = encryptData(cardData.cardCcv);
    const encryptedCardExp = encryptData(cardData.cardExp);
    
    // Prepare data for API
    const data = {
      alias: cardData.alias,
      cardNumber: encryptedCardNumber,
      cardCcv: encryptedCardCcv,
      cardExp: encryptedCardExp,
      cardHolderName: cardData.cardHolderName || '',
      street: cardData.street || '',
      city: cardData.city || '',
      state: cardData.state || '',
      zip: cardData.zip || '',
      country: cardData.country || '',
      accountNumber: cardData.accountNumber || ''
    };
    
    // Send to API
    const response = await fetch(UPLOAD_ENDPOINTS.UPLOAD_CARD, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    // Clear cache after successful save
    await clearBucketContentCache();
    
    return true;
  } catch (error) {
    console.error('Error saving bank card:', error);
    throw error;
  }
};

// Delete bank card
export const deleteBankCard = async (cardName, token) => {
  try {
    // First, update any inheritances that contain this bank card
    try {
      await updateInheritancesAfterBankCardDeletion(cardName, token);
    } catch (error) {
      console.error('Error updating inheritances:', error);
      // Continue with deletion even if inheritance update fails
    }
    
    return await deleteFile(BUCKET_NAME, cardName, token);
  } catch (error) {
    console.error('Error deleting bank card:', error);
    throw error;
  }
};

// Check if bank card alias already exists
export const checkBankCardAliasExists = async (alias, token) => {
  try {
    const cards = await fetchBankCards(token);
    return cards.some(card => 
      removeTxtExtension(card.name).toLowerCase() === alias.toLowerCase()
    );
  } catch (error) {
    console.error('Error checking bank card alias:', error);
    throw error;
  }
};

// Generate unique bank card alias
export const generateUniqueBankCardAlias = async (proposedAlias, token) => {
  try {
    // Make sure we're working with a clean alias (no .txt extension)
    proposedAlias = removeTxtExtension(proposedAlias.trim());
    proposedAlias = ensureS3SafeFileName(proposedAlias);
    
    if (proposedAlias === '') {
      return 'Untitled';
    }
    
    const cards = await fetchBankCards(token);
    
    // Get all existing bank card aliases without .txt extension for comparison
    const existingAliases = cards.map(card => 
      removeTxtExtension(card.name).toLowerCase()
    );
    
    // Check if the base alias already exists
    if (!existingAliases.includes(proposedAlias.toLowerCase())) {
      return proposedAlias;
    }
    
    // Name exists, find a unique variation
    let counter = 1;
    let uniqueAlias = `${proposedAlias}(${counter})`;
    
    // Keep incrementing counter until we find a unique name
    while (existingAliases.includes(uniqueAlias.toLowerCase())) {
      counter++;
      uniqueAlias = `${proposedAlias}(${counter})`;
    }
    
    return uniqueAlias;
  } catch (error) {
    console.error('Error generating unique alias:', error);
    throw error;
  }
};

// Check if expiration date format is valid (MM/YY)
export const isValidExpDate = (expDate) => {
  if (expDate.length !== 5) {
    return false;
  }
  
  const regex = /^(0[1-9]|1[0-2])\/\d{2}$/;
  if (!regex.test(expDate)) {
    return false;
  }
  
  const [month, year] = expDate.split('/');
  
  const expMonth = parseInt(month, 10);
  const expYear = parseInt(year, 10);
  
  if (expMonth < 1 || expMonth > 12) {
    return false;
  }
  
  if (expYear < 0 || expYear > 99) {
    return false;
  }
  
  return true;
};

/**
 * Add bank card to the user's current inheritance (like endowment page)
 * @param {string} cardName - Bank card name/filename
 * @param {string} token - Authentication token
 * @returns {Promise<boolean>} Success status
 */
export const addCardToCurrentInheritance = async (cardName, token) => {
  try {
    if (!token) {
      const credentials = getUserCredentials();
      if (!credentials) {
        throw new Error('No authentication information found');
      }
      token = credentials.token;
    }

    const userDetails = JSON.parse(sessionStorage.getItem('userDetails') || '{}');
    const userEmail = userDetails.Email || '';
    
    if (!userEmail) {
      throw new Error('User email not found in session');
    }

    // Get the current (most recent) inheritance for this user
    const currentInheritance = await getCurrentInheritance(token, userEmail);
    
    if (!currentInheritance) {
      throw new Error('No active endowment found. Please create an endowment first in the Endowment Centre.');
    }

    // Use the card name as filename (should already have .txt extension)
    const cardToAdd = ensureTxtExtension(cardName);

    // Check if the card is already in the inheritance
    if (currentInheritance.selectedItems.banking && currentInheritance.selectedItems.banking.includes(cardToAdd)) {
      throw new Error('Bank card is already added to this endowment');
    }

    // Add the card to the existing banking items (same logic as handleAddItems in useEndowment)
    const updatedItems = {
      files: [...(currentInheritance.selectedItems.files || [])],
      passwords: [...(currentInheritance.selectedItems.passwords || [])],
      banking: [...(currentInheritance.selectedItems.banking || []), cardToAdd],  // Add the new card
      blockchain: [...(currentInheritance.selectedItems.blockchain || [])]
    };

    // Use uploadInheritance to update the inheritance with the new card
    // This works exactly like handleAddItems in useEndowment.js
    await uploadInheritance({
      alias: currentInheritance.alias,
      account: currentInheritance.account,
      selectedItems: updatedItems,
      duration: currentInheritance.duration,
      Email: userEmail
    }, token);

    return true;
  } catch (error) {
    console.error('❌ Error adding card to current inheritance:', error);
    throw error;
  }
};

// Update inheritances after bank card deletion
const updateInheritancesAfterBankCardDeletion = async (cardName, token) => {
  try {
    if (!token) {
      const credentials = getUserCredentials();
      if (!credentials) {
        throw new Error('No authentication information found');
      }
      token = credentials.token;
    }

    const userDetails = JSON.parse(sessionStorage.getItem('userDetails') || '{}');
    const userEmail = userDetails.Email || '';
    
    if (!userEmail) {
      throw new Error('User email not found in session');
    }


    // Get the current inheritance for this user
    const currentInheritance = await getCurrentInheritance(token, userEmail);
    
    if (!currentInheritance) {
      return true;
    }

    const cardToRemove = ensureTxtExtension(cardName);
    const cardIndex = currentInheritance.selectedItems.banking?.indexOf(cardToRemove) ?? -1;
    
    if (cardIndex === -1) {
      return true;
    }
    
    
    // Remove the card from the inheritance
    const updatedItems = {
      files: [...(currentInheritance.selectedItems.files || [])],
      passwords: [...(currentInheritance.selectedItems.passwords || [])],
      banking: currentInheritance.selectedItems.banking.filter(card => card !== cardToRemove),
      blockchain: [...(currentInheritance.selectedItems.blockchain || [])]
    };
    
    // Use uploadInheritance to update the inheritance without the deleted card
    await uploadInheritance({
      alias: currentInheritance.alias,
      account: currentInheritance.account,
      selectedItems: updatedItems,
      duration: currentInheritance.duration,
      Email: userEmail
    }, token);
    
    return true;
  } catch (error) {
    console.error('❌ Error updating inheritance after bank card deletion:', error);
    throw error;
  }
};

// Get inheritance list (placeholder)
export const getInheritanceList = async (token) => {
  try {
    const response = await fetch(CONTENT_ENDPOINTS.BUCKET_CONTENT_PAGE, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ bucket: "inheritance" })
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    const transformedData = result.filesMetadata.map((file, index) => ({
      id: index + 1,
      name: file.Key,
      size: file.Size,
      lastModified: file.LastModified
    }));
    
    return transformedData;
  } catch (error) {
    console.error('Error fetching inheritance list:', error);
    throw error;
  }
};

// Get inheritance content
export const getInheritance = async (inheritanceName, token) => {
  try {
    return await fetchFileContent("inheritance", inheritanceName, token);
  } catch (error) {
    console.error('Error getting inheritance:', error);
    throw error;
  }
};

// Add bank card to inheritance
export const addCardToInheritance = async (cardName, inheritanceName, token) => {
  try {
    const fileContent = await getInheritance(inheritanceName, token);
    
    return true;
  } catch (error) {
    console.error('Error adding card to inheritance:', error);
    throw error;
  }
};